import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { EFFECT_SECTION_KNOBS, type EffectSection } from '@/lib/video-effects/sections';
import { EFFECT_LIBRARY } from '@/lib/video-effects/library/registry';

/** Controls for the selected timeline effect. Sliders keep a local draft and
 *  commit on release so a drag is one undo entry and one save. */
export function EffectSectionControls({ section, onChange, onDelete }: {
  section: EffectSection;
  onChange: (patch: Partial<EffectSection>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const knob = EFFECT_SECTION_KNOBS[section.effect];
  const [intensity, setIntensity] = useState(section.intensity);
  const knobValue = knob ? section.params?.[knob.key] ?? knob.default : 0;
  const [knobDraft, setKnobDraft] = useState(knobValue);
  useEffect(() => setIntensity(section.intensity), [section.id, section.intensity]);
  useEffect(() => setKnobDraft(knobValue), [section.id, knobValue]);
  return (
    <div data-layer-ui className="flex flex-wrap items-end gap-4 rounded-md border border-border bg-card px-3 py-2 text-xs">
      <span className="self-center font-medium">{t(EFFECT_LIBRARY[section.effect].labelKey)}</span>
      <label className="flex w-40 flex-col gap-1">
        <span className="text-muted-foreground">{t('videoEffects.sections.intensity')} · {Math.round(intensity)}</span>
        <Slider data-testid="effect-section-intensity" min={0} max={100} step={1} value={[intensity]}
          onValueChange={([v]) => setIntensity(v)} onValueCommit={([v]) => onChange({ intensity: v })} />
      </label>
      {knob && (
        <label className="flex w-40 flex-col gap-1">
          <span className="text-muted-foreground">{t(`videoEffects.sections.${knob.key}`)} · {knobDraft.toFixed(1)}</span>
          <Slider data-testid={`effect-section-knob-${knob.key}`} min={knob.min} max={knob.max} step={0.1} value={[knobDraft]}
            onValueChange={([v]) => setKnobDraft(v)} onValueCommit={([v]) => onChange({ params: { [knob.key]: v } })} />
        </label>
      )}
      <label className="flex items-center gap-2 self-center">
        <Switch data-testid="effect-section-ease" checked={section.ease} onCheckedChange={(v) => onChange({ ease: v })} />
        <span>{t('videoEffects.sections.ease')}</span>
      </label>
      <Button data-testid="effect-section-delete" variant="ghost" size="sm" aria-label={t('videoEffects.sections.delete')} onClick={onDelete}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
